<template>
  <Layout>
    <div class="badge-builder-page">
      <!-- Header -->
      <div class="builder-header">
        <div class="header-left">
          <button class="btn-back" @click="$router.push('/badge-builder')">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="template-title-section">
            <input
              v-model="templateData.name"
              type="text"
              class="template-title-input"
              placeholder="Nome do Template"
            />
            <span class="template-status" :class="templateData.is_active ? 'active' : 'inactive'">
              {{ templateData.is_active ? 'Ativo' : 'Inativo' }}
            </span>
          </div>
        </div>

        <div class="header-center">
          <div class="config-group">
            <label>Impressora</label>
            <select v-model="selectedPrinter" @change="onPrinterChange">
              <option value="">Selecione...</option>
              <option v-for="printer in printers" :key="printer" :value="printer">
                {{ printer }}
              </option>
            </select>
          </div>
          <div class="config-group" v-if="selectedPrinter">
            <label>Tamanho</label>
            <select v-model="templateData.label_size_id" @change="onLabelSizeChange">
              <option :value="null">Selecione...</option>
              <option
                v-for="size in filteredLabelSizes"
                :key="size.id"
                :value="size.id"
              >
                {{ size.name }}
              </option>
            </select>
          </div>
          <div class="config-group orientation-group" v-if="templateData.label_size_id">
            <label>Orientacao</label>
            <div class="orientation-btns">
              <button
                type="button"
                class="orientation-btn"
                :class="{ active: templateData.orientation === 'portrait' }"
                @click="setOrientation('portrait')"
                title="Vertical"
              >
                <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
                  <rect x="1" y="1" width="12" height="16" rx="2" stroke="currentColor" stroke-width="1.5" fill="none"/>
                </svg>
              </button>
              <button
                type="button"
                class="orientation-btn"
                :class="{ active: templateData.orientation === 'landscape' }"
                @click="setOrientation('landscape')"
                title="Horizontal"
              >
                <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                  <rect x="1" y="1" width="16" height="12" rx="2" stroke="currentColor" stroke-width="1.5" fill="none"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div class="header-right">
          <button class="btn-icon" @click="undo" :disabled="!canUndo" title="Desfazer (Ctrl+Z)">
            <i class="bx bx-undo"></i>
          </button>
          <button class="btn-icon" @click="redo" :disabled="!canRedo" title="Refazer (Ctrl+Y)">
            <i class="bx bx-redo"></i>
          </button>
          <span class="header-divider"></span>
          <button class="btn-secondary" @click="printTest">
            <i class="bx bx-printer"></i>
            Testar
          </button>
          <button class="btn-save" @click="saveTemplate" :disabled="saving">
            <i class="bx" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>

      <!-- Conteudo Principal -->
      <div class="builder-content">
        <!-- Sidebar Esquerda - Modelos Pré-definidos -->
        <div class="sidebar-left">
          <div class="sidebar-header">
            <h3>Modelos</h3>
          </div>
          <div class="search-box">
            <i class="bx bx-search"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Buscar modelos..."
            />
          </div>
          <div class="templates-list">
            <div
              v-for="preset in filteredPresets"
              :key="preset.id"
              class="template-card preset-card"
              @click="applyPreset(preset)"
              title="Clique para aplicar este modelo"
            >
              <div class="template-header">
                <div class="template-info">
                  <h4>{{ preset.name }}</h4>
                  <span class="template-size">{{ preset.width }}x{{ preset.height }}mm</span>
                </div>
              </div>
              <div class="template-preview">
                <img
                  v-if="preset.thumbnail_url"
                  :src="preset.thumbnail_url"
                  :alt="preset.name"
                  class="preset-thumbnail"
                />
                <div v-else class="preview-badge" :style="getPreviewStyle(preset)">
                  <i class="bx bx-id-card"></i>
                </div>
              </div>
              <div class="template-footer">
                <span class="preset-hint"><i class="bx bx-download"></i> Aplicar modelo</span>
              </div>
            </div>
            <div v-if="filteredPresets.length === 0" class="empty-state">
              <i class="bx bx-box"></i>
              <p>Nenhum modelo encontrado</p>
            </div>
          </div>
        </div>

        <!-- Area do Canvas -->
        <div class="canvas-area">
          <!-- Barra de Ferramentas do Canvas -->
          <div class="canvas-toolbar">
            <div class="toolbar-left">
              <button class="toolbar-btn" @click="zoomOut" title="Diminuir Zoom">
                <i class="bx bx-minus"></i>
              </button>
              <span class="zoom-level">{{ Math.round(zoom * 100) }}%</span>
              <button class="toolbar-btn" @click="zoomIn" title="Aumentar Zoom">
                <i class="bx bx-plus"></i>
              </button>
              <button class="toolbar-btn" @click="resetZoom" title="Zoom 100%">
                <i class="bx bx-reset"></i>
              </button>
            </div>
            <div class="toolbar-right">
              <label class="grid-toggle">
                <input type="checkbox" v-model="showGrid" />
                Grade: {{ showGrid ? 'Visivel' : 'Oculta' }}
              </label>
            </div>
          </div>

          <!-- Container do Canvas -->
          <div class="canvas-container" ref="canvasContainer">
            <!-- Regua Horizontal -->
            <div class="ruler ruler-horizontal" v-if="templateData.width">
              <div
                v-for="n in rulerMarksH"
                :key="'h-' + n"
                class="ruler-mark"
                :style="{ left: (n * 10 * zoom) + 'px' }"
              >
                <span v-if="n % 5 === 0">{{ n }}mm</span>
              </div>
            </div>

            <!-- Regua Vertical -->
            <div class="ruler ruler-vertical" v-if="templateData.height">
              <div
                v-for="n in rulerMarksV"
                :key="'v-' + n"
                class="ruler-mark"
                :style="{ top: (n * 10 * zoom) + 'px' }"
              >
                <span v-if="n % 5 === 0">{{ n }}</span>
              </div>
            </div>

            <!-- Canvas da Etiqueta -->
            <div
              v-if="templateData.width && templateData.height"
              class="badge-canvas"
              ref="badgeCanvas"
              :style="canvasStyle"
              @dragover.prevent="onDragOver"
              @drop="onDrop"
              @click="deselectElement"
            >
              <!-- Grade -->
              <div v-if="showGrid" class="grid-overlay" :style="gridStyle"></div>

              <!-- Elementos -->
              <div
                v-for="element in templateData.elements"
                :key="element.id"
                class="canvas-element"
                :class="{
                  selected: selectedElement && selectedElement.id === element.id,
                  locked: element.is_locked,
                  hidden: !element.is_visible
                }"
                :style="getElementStyle(element)"
                @click.stop="selectElement(element)"
                @mousedown="startDrag($event, element)"
              >
                <!-- Conteudo do Elemento -->
                <div class="element-content" :style="getElementContentStyle(element)">
                  <template v-if="element.type === 'text'">
                    {{ element.content || 'Texto' }}
                  </template>
                  <template v-else-if="element.type === 'dynamic_field'">
                    {{ getFieldPreviewValue(element) }}
                  </template>
                  <template v-else-if="element.type === 'qrcode'">
                    <img :src="qrcodePreviewImage" :style="{ objectFit: 'contain', width: '100%', height: '100%' }" />
                  </template>
                  <template v-else-if="element.type === 'photo'">
                    <img v-if="getParticipantPhotoUrl()" :src="getParticipantPhotoUrl()" :style="{ objectFit: element.object_fit || 'cover', width: '100%', height: '100%' }" />
                    <div v-else class="photo-placeholder">
                      <i class="bx bx-camera" style="font-size: 2rem;"></i>
                      <small>Foto</small>
                    </div>
                  </template>
                  <template v-else-if="element.type === 'image'">
                    <img v-if="element.image_url" :src="element.image_url" :style="{ objectFit: element.object_fit || 'contain', width: '100%', height: '100%' }" />
                    <i v-else class="bx bx-image" style="font-size: 2rem;"></i>
                  </template>
                  <template v-else-if="element.type === 'shape'">
                    <div :style="getShapeStyle(element)"></div>
                  </template>
                  <template v-else-if="element.type === 'line'">
                    <div :style="{ width: '100%', height: '2px', backgroundColor: element.border_color || '#000' }"></div>
                  </template>
                </div>

                <!-- Alças de Redimensionamento -->
                <template v-if="selectedElement && selectedElement.id === element.id && !element.is_locked">
                  <div class="resize-handle nw" @mousedown.stop="startResize($event, element, 'nw')"></div>
                  <div class="resize-handle ne" @mousedown.stop="startResize($event, element, 'ne')"></div>
                  <div class="resize-handle sw" @mousedown.stop="startResize($event, element, 'sw')"></div>
                  <div class="resize-handle se" @mousedown.stop="startResize($event, element, 'se')"></div>
                </template>
              </div>
            </div>

            <!-- Mensagem quando nao ha tamanho selecionado -->
            <div v-else class="no-size-selected">
              <i class="bx bx-info-circle"></i>
              <p>Selecione uma impressora e tamanho de etiqueta para comecar</p>
            </div>
          </div>

          <!-- Rodape do Canvas -->
          <div class="canvas-footer">
            <div class="footer-left">
              <span>{{ templateData.elements.length }} elementos</span>
              <span class="separator">|</span>
              <span>{{ lastSavedText }}</span>
            </div>
            <div class="footer-right">
              <span v-if="templateData.width">{{ templateData.width }}mm x {{ templateData.height }}mm</span>
              <span class="separator">|</span>
              <span>Grade: {{ gridSize }}px</span>
            </div>
          </div>
        </div>

        <!-- Sidebar Direita - Elementos e Propriedades -->
        <div class="sidebar-right">
          <div class="sidebar-tabs">
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'elements' }"
              @click="activeTab = 'elements'"
            >
              <i class="bx bx-layer"></i> Elementos
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'properties' }"
              @click="activeTab = 'properties'"
            >
              <i class="bx bx-cog"></i> Propriedades
            </button>
          </div>

          <!-- Aba Elementos -->
          <div v-show="activeTab === 'elements'" class="tab-content">
            <!-- Adicionar Elementos -->
            <div class="panel-section">
              <h4>ADICIONAR ELEMENTOS</h4>
              <div class="elements-grid">
                <div
                  v-for="el in elementTypes"
                  :key="el.type"
                  class="element-item"
                  draggable="true"
                  @dragstart="onElementDragStart($event, el)"
                  @click="addElementAtCenter(el.type)"
                >
                  <i :class="el.icon"></i>
                  <span>{{ el.label }}</span>
                </div>
              </div>
            </div>

            <!-- Campos Dinamicos -->
            <div class="panel-section panel-section-grow">
              <h4>CAMPOS DINAMICOS</h4>
              <div v-if="loadingFields" class="loading-fields">
                <i class="bx bx-loader-alt bx-spin"></i> Carregando campos...
              </div>
              <div v-else class="dynamic-fields-list">
                <div
                  v-for="field in dynamicFields"
                  :key="field.key"
                  class="field-item"
                  draggable="true"
                  @dragstart="onFieldDragStart($event, field)"
                  @click="addDynamicFieldAtCenter(field)"
                >
                  <span class="field-name">{{ field.label }}</span>
                  <button class="btn-add-field" title="Adicionar campo">
                    <i class="bx bx-plus"></i>
                  </button>
                </div>
              </div>
            </div>

          </div>

          <!-- Aba Propriedades -->
          <div v-show="activeTab === 'properties'" class="tab-content">
            <div v-if="selectedElement" class="properties-panel">
              <!-- Nome do Elemento -->
              <div class="panel-section">
                <h4>ELEMENTO</h4>
                <div class="form-group">
                  <label>Nome</label>
                  <input type="text" v-model="selectedElement.name" @input="updateElement" placeholder="Nome do elemento" />
                </div>
              </div>

              <!-- Posicao e Tamanho -->
              <div class="panel-section">
                <h4>POSICAO E TAMANHO</h4>
                <div class="properties-grid">
                  <div class="prop-item">
                    <label>X (px)</label>
                    <input type="number" v-model.number="selectedElement.position_x" @input="updateElement" />
                  </div>
                  <div class="prop-item">
                    <label>Y (px)</label>
                    <input type="number" v-model.number="selectedElement.position_y" @input="updateElement" />
                  </div>
                  <div class="prop-item">
                    <label>Largura</label>
                    <input type="number" v-model.number="selectedElement.width" @input="updateElement" />
                  </div>
                  <div class="prop-item">
                    <label>Altura</label>
                    <input type="number" v-model.number="selectedElement.height" @input="updateElement" />
                  </div>
                </div>
              </div>

              <!-- Propriedades de Texto -->
              <div v-if="selectedElement.type === 'text' || selectedElement.type === 'dynamic_field'" class="panel-section">
                <h4>TEXTO</h4>
                <div v-if="selectedElement.type === 'text'" class="form-group">
                  <label>Conteudo</label>
                  <input type="text" v-model="selectedElement.content" @input="updateElement" placeholder="Texto" />
                </div>
                <div class="form-group">
                  <label>Fonte</label>
                  <select v-model="selectedElement.font_family" @change="updateElement">
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Verdana">Verdana</option>
                    <option value="Roboto">Roboto</option>
                  </select>
                </div>
                <div class="properties-grid">
                  <div class="prop-item">
                    <label>Tamanho</label>
                    <input type="number" v-model.number="selectedElement.font_size" @input="updateElement" />
                  </div>
                  <div class="prop-item">
                    <label>Cor</label>
                    <input type="color" v-model="selectedElement.font_color" @input="updateElement" />
                  </div>
                </div>
                <div class="form-group">
                  <label>Alinhamento</label>
                  <div class="btn-group-props">
                    <button
                      type="button"
                      class="btn-prop"
                      :class="{ active: selectedElement.text_align === 'left' }"
                      @click="selectedElement.text_align = 'left'; updateElement()"
                    >
                      <i class="bx bx-align-left"></i>
                    </button>
                    <button
                      type="button"
                      class="btn-prop"
                      :class="{ active: selectedElement.text_align === 'center' }"
                      @click="selectedElement.text_align = 'center'; updateElement()"
                    >
                      <i class="bx bx-align-middle"></i>
                    </button>
                    <button
                      type="button"
                      class="btn-prop"
                      :class="{ active: selectedElement.text_align === 'right' }"
                      @click="selectedElement.text_align = 'right'; updateElement()"
                    >
                      <i class="bx bx-align-right"></i>
                    </button>
                  </div>
                </div>
                <div class="form-group">
                  <label>Estilo</label>
                  <div class="btn-group-props">
                    <button
                      type="button"
                      class="btn-prop"
                      :class="{ active: selectedElement.font_bold }"
                      @click="selectedElement.font_bold = !selectedElement.font_bold; updateElement()"
                    >
                      <i class="bx bx-bold"></i>
                    </button>
                    <button
                      type="button"
                      class="btn-prop"
                      :class="{ active: selectedElement.font_italic }"
                      @click="selectedElement.font_italic = !selectedElement.font_italic; updateElement()"
                    >
                      <i class="bx bx-italic"></i>
                    </button>
                    <button
                      type="button"
                      class="btn-prop"
                      :class="{ active: selectedElement.font_underline }"
                      @click="selectedElement.font_underline = !selectedElement.font_underline; updateElement()"
                    >
                      <i class="bx bx-underline"></i>
                    </button>
                    <button
                      type="button"
                      class="btn-prop"
                      :class="{ active: selectedElement.text_transform === 'uppercase' }"
                      title="Imprimir em MAIÚSCULAS"
                      @click="selectedElement.text_transform = selectedElement.text_transform === 'uppercase' ? 'none' : 'uppercase'; updateElement()"
                    >
                      <i class="bx bx-font-size"></i> AA
                    </button>
                  </div>
                </div>
                <div class="form-group">
                  <label>Ajuste do texto</label>
                  <select v-model="selectedElement.text_fit" @change="updateElement">
                    <option value="box">Ajustar à caixa (reduz a fonte)</option>
                    <option value="shrink">Linha única (reduz a fonte se estourar)</option>
                    <option value="wrap">Quebrar linha (mantém a fonte, cresce p/ baixo)</option>
                  </select>
                  <small v-if="selectedElement.text_fit === 'wrap'" class="prop-hint">
                    O elemento cresce para baixo e empurra os que estiverem abaixo, sem sobrepor.
                  </small>
                </div>
              </div>

              <!-- Propriedades de Imagem -->
              <div v-if="selectedElement.type === 'image' || selectedElement.type === 'logo'" class="panel-section">
                <h4>IMAGEM</h4>
                <div class="form-group">
                  <label>URL da Imagem</label>
                  <input type="text" v-model="selectedElement.image_url" @input="updateElement" placeholder="https://..." />
                </div>
                <div class="form-group">
                  <label>Ajuste</label>
                  <select v-model="selectedElement.object_fit" @change="updateElement">
                    <option value="contain">Conter</option>
                    <option value="cover">Cobrir</option>
                    <option value="fill">Preencher</option>
                  </select>
                </div>
              </div>

              <!-- Propriedades de QR/Codigo de Barras -->
              <div v-if="selectedElement.type === 'qrcode' || selectedElement.type === 'barcode'" class="panel-section">
                <h4>{{ selectedElement.type === 'qrcode' ? 'QR CODE' : 'CODIGO DE BARRAS' }}</h4>
                <div class="form-group">
                  <label>Fonte de Dados</label>
                  <select v-model="selectedElement.data_source" @change="updateElement">
                    <option value="badge_id">ID do Cracha</option>
                    <option value="participant_id">ID do Participante</option>
                    <option value="email">E-mail</option>
                    <option value="cpf">CPF</option>
                    <option value="custom">Personalizado</option>
                  </select>
                </div>
                <div v-if="selectedElement.data_source === 'custom'" class="form-group">
                  <label>Valor Personalizado</label>
                  <input type="text" v-model="selectedElement.custom_value" @input="updateElement" />
                </div>
              </div>

              <!-- Propriedades de Forma -->
              <div v-if="selectedElement.type === 'shape'" class="panel-section">
                <h4>FORMA</h4>
                <div class="form-group">
                  <label>Tipo</label>
                  <select v-model="selectedElement.shape_type" @change="updateElement">
                    <option value="rectangle">Retangulo</option>
                    <option value="circle">Circulo</option>
                    <option value="rounded">Retangulo Arredondado</option>
                  </select>
                </div>
                <div class="properties-grid">
                  <div class="prop-item">
                    <label>Preenchimento</label>
                    <input type="color" v-model="selectedElement.fill_color" @input="updateElement" />
                  </div>
                  <div class="prop-item">
                    <label>Borda</label>
                    <input type="color" v-model="selectedElement.border_color" @input="updateElement" />
                  </div>
                </div>
              </div>

              <!-- Botao Excluir -->
              <div class="panel-section">
                <button type="button" class="btn-danger" @click="deleteElement">
                  <i class="bx bx-trash"></i> Excluir Elemento
                </button>
              </div>
            </div>

            <div v-else class="no-selection">
              <i class="bx bx-pointer"></i>
              <p>Selecione um elemento para editar suas propriedades</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import * as services from './services'
import participantCardServices from '../participant-card-builder/services'
import { successMessage, errorMessage } from '@/components/alertsMessages'
import qrcodeImage from '@/assets/images/qrcode.png'

export default {
  name: 'BadgeBuilderEditor',
  components: {
    Layout
  },
  data: function () {
    return {
      loading: false,
      saving: false,
      loadingFields: false,
      searchQuery: '',
      selectedTemplateId: null,
      activeMenuTemplateId: null,
      selectedPrinter: '',
      templates: [],
      presets: [],
      labelSizes: [],
      printers: [],
      qrcodePreviewImage: qrcodeImage,
      templateData: {
        id: null,
        name: 'Novo Template',
        printer_model: '',
        label_size_id: null,
        orientation: 'landscape',
        width: null,
        height: null,
        background_color: '#FFFFFF',
        background_image: null,
        is_active: true,
        elements: []
      },
      selectedElement: null,
      activeTab: 'elements',
      zoom: 1,
      showGrid: true,
      gridSize: 10,
      lastSaved: null,
      canUndo: false,
      canRedo: false,
      history: [],
      historyIndex: -1,
      isDragging: false,
      isResizing: false,
      dragStart: { x: 0, y: 0 },
      resizeHandle: null,
      resizeStart: null,
      elementTypes: [
        { type: 'text', label: 'Texto', icon: 'bx bx-text' },
        { type: 'image', label: 'Imagem', icon: 'bx bx-image' },
        { type: 'qrcode', label: 'QR Code', icon: 'bx bx-grid-alt' },
        { type: 'photo', label: 'Foto', icon: 'bx bx-camera' },
        { type: 'shape', label: 'Forma', icon: 'bx bx-square' },
        { type: 'line', label: 'Linha', icon: 'bx bx-minus' }
      ],
      // Campos da ficha do participante (carregados da API)
      participantCardFields: [],
      // Campos dos controles de inscrição ativos (catálogo do backend)
      registrationControlFields: [],
      // Campos fixos - sempre disponíveis (standards do whitelist do backend)
      fixedFields: [
        { key: 'badge_name', label: 'Nome para Crachá', field_type: 'standard', field_source_id: null, standard_field_name: 'badge_name' },
        { key: 'group_name', label: 'Grupo', field_type: 'standard', field_source_id: null, standard_field_name: 'group_name' }
      ]
    }
  },
  computed: {
    filteredTemplates: function () {
      var self = this
      if (!self.searchQuery) return self.templates
      var query = self.searchQuery.toLowerCase()
      return self.templates.filter(function (t) {
        return t.name.toLowerCase().indexOf(query) !== -1
      })
    },
    filteredPresets: function () {
      var self = this
      if (!self.searchQuery) return self.presets
      var query = self.searchQuery.toLowerCase()
      return self.presets.filter(function (p) {
        return p.name.toLowerCase().indexOf(query) !== -1
      })
    },
    filteredLabelSizes: function () {
      var self = this
      if (!self.selectedPrinter) return []
      return self.labelSizes.filter(function (size) {
        return size.printer_model === self.selectedPrinter
      })
    },
    dynamicFields: function () {
      // Combinar campos da ficha + fixos + controles de inscrição ativos
      var allFields = this.participantCardFields.slice()
      this.fixedFields.forEach(function (field) {
        allFields.push(field)
      })
      this.registrationControlFields.forEach(function (field) {
        allFields.push(field)
      })
      return allFields
    },
    canvasStyle: function () {
      var width = this.templateData.width || 100
      var height = this.templateData.height || 62
      // Converter mm para pixels (aproximadamente 3.78 pixels por mm)
      var pixelWidth = width * 3.78
      var pixelHeight = height * 3.78
      return {
        width: (pixelWidth * this.zoom) + 'px',
        height: (pixelHeight * this.zoom) + 'px',
        backgroundColor: this.templateData.background_color || '#FFFFFF',
        backgroundImage: this.templateData.background_image ? 'url(' + this.templateData.background_image + ')' : 'none',
        backgroundSize: 'cover'
      }
    },
    gridStyle: function () {
      var size = this.gridSize * this.zoom
      return {
        backgroundSize: size + 'px ' + size + 'px',
        backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.08) 1px, transparent 1px)'
      }
    },
    rulerMarksH: function () {
      return Math.ceil((this.templateData.width || 100) / 10) + 1
    },
    rulerMarksV: function () {
      return Math.ceil((this.templateData.height || 62) / 10) + 1
    },
    reversedElements: function () {
      return this.templateData.elements.slice().reverse()
    },
    lastSavedText: function () {
      if (!this.lastSaved) return 'Nao salvo'
      return 'Salvo ' + this.formatTimeAgo(this.lastSaved)
    }
  },
  mounted: function () {
    document.body.classList.add('badge-builder-page')

    this.loadTemplates()
    this.loadPresets()
    this.loadLabelSizes()
    this.loadParticipantCardFields()
    this.loadRegistrationControlFields()

    var templateId = this.$route.params.id
    if (templateId) {
      this.loadTemplate(templateId)
    }

    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('mouseup', this.onMouseUp)
    document.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('click', this.handleClickOutsideMenu)
  },
  beforeDestroy: function () {
    document.body.classList.remove('badge-builder-page')

    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
    document.removeEventListener('keydown', this.onKeyDown)
    document.removeEventListener('click', this.handleClickOutsideMenu)
  },
  methods: {
    // Conversão de elementos entre frontend e backend
    elementToBackend: function (element) {
      // Agrupa propriedades visuais em 'styles' e configurações em 'config'
      return {
        id: typeof element.id === 'string' && element.id.startsWith('temp_') ? null : element.id,
        type: element.type,
        field_type: element.field_type || null,
        field_source_id: element.field_source_id || null,
        // standard_field_name carrega o identificador do campo quando não há
        // field_source_id: standard (name, cpf...) e registration_control (a
        // chave do resolver, ex. rc_5_field_escola). form_field/card_field usam
        // field_source_id, então aqui vai null.
        standard_field_name: (element.field_type === 'standard' || element.field_type === 'registration_control')
          ? (element.standard_field_name || element.field_key || null)
          : null,
        position_x: element.position_x,
        position_y: element.position_y,
        width: element.width,
        height: element.height,
        rotation: element.rotation || 0,
        z_index: element.z_index,
        content: element.content || null,
        is_locked: element.is_locked || false,
        is_visible: element.is_visible !== false,
        styles: {
          font_family: element.font_family,
          font_size: element.font_size,
          font_color: element.font_color,
          font_bold: element.font_bold,
          font_italic: element.font_italic,
          font_underline: element.font_underline,
          text_transform: element.text_transform || 'none',
          text_fit: element.text_fit || 'box',
          text_align: element.text_align,
          fill_color: element.fill_color,
          border_color: element.border_color,
          border_width: element.border_width,
          object_fit: element.object_fit
        },
        config: {
          name: element.name,
          image_url: element.image_url,
          data_source: element.data_source,
          custom_value: element.custom_value,
          shape_type: element.shape_type
        }
      }
    },
    elementFromBackend: function (element) {
      // Expande 'styles' e 'config' para propriedades individuais
      // Tratar caso onde styles/config podem vir como string JSON
      var styles = element.styles || {}
      var config = element.config || {}

      // Se styles for string, fazer parse
      if (typeof styles === 'string') {
        try {
          styles = JSON.parse(styles)
        } catch (e) {
          styles = {}
        }
      }

      // Se config for string, fazer parse
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          config = {}
        }
      }
      return {
        id: element.id,
        type: element.type,
        field_type: element.field_type,
        field_source_id: element.field_source_id,
        field_key: element.standard_field_name,
        standard_field_name: element.standard_field_name,
        position_x: parseFloat(element.position_x) || 0,
        position_y: parseFloat(element.position_y) || 0,
        width: parseFloat(element.width) || 80,
        height: parseFloat(element.height) || 60,
        rotation: parseFloat(element.rotation) || 0,
        z_index: element.z_index || 0,
        content: element.content || '',
        is_locked: element.is_locked || false,
        is_visible: element.is_visible !== false,
        // Estilos
        font_family: styles.font_family || 'Arial',
        font_size: styles.font_size || 12,
        font_color: styles.font_color || '#000000',
        font_bold: styles.font_bold || false,
        font_italic: styles.font_italic || false,
        font_underline: styles.font_underline || false,
        text_transform: styles.text_transform || 'none',
        text_fit: styles.text_fit || 'box',
        text_align: styles.text_align || 'left',
        fill_color: styles.fill_color || '#FFFFFF',
        border_color: styles.border_color || '#000000',
        border_width: styles.border_width || 1,
        object_fit: styles.object_fit || 'contain',
        // Configurações
        name: config.name || '',
        image_url: config.image_url || '',
        data_source: config.data_source || 'badge_id',
        custom_value: config.custom_value || '',
        shape_type: config.shape_type || 'rectangle'
      }
    },

    // Helpers para tratar resposta da API
    extractData: function (response) {
      if (!response) return null
      var data = response.data
      if (data && data.data) {
        return data.data
      }
      return data
    },
    extractArray: function (response) {
      var data = this.extractData(response)
      if (Array.isArray(data)) {
        return data
      }
      return []
    },
    // Carregamento de Dados
    loadTemplates: function () {
      var self = this
      services.listTemplates()
        .then(function (response) {
          self.templates = self.extractArray(response)
        })
        .catch(function (error) {
          console.error('Erro ao carregar templates:', error)
        })
    },
    loadPresets: function () {
      var self = this
      services.listPresets()
        .then(function (response) {
          self.presets = self.extractArray(response)
        })
        .catch(function (error) {
          console.error('Erro ao carregar modelos:', error)
        })
    },
    applyPreset: function (preset) {
      var self = this

      // Aplicar configurações do preset ao template atual
      self.templateData.width = parseFloat(preset.width)
      self.templateData.height = parseFloat(preset.height)
      self.templateData.orientation = preset.orientation
      self.templateData.background_color = preset.background_color
      self.templateData.label_size_id = preset.label_size_id
      self.templateData.printer_model = preset.printer_model

      // Extrair elementos do preset (pode vir em diferentes formatos da API)
      var presetElements = []
      if (preset.elements) {
        if (preset.elements.data && Array.isArray(preset.elements.data)) {
          presetElements = preset.elements.data
        } else if (Array.isArray(preset.elements)) {
          presetElements = preset.elements
        }
      }

      // Criar novos elementos a partir do preset
      var newElements = []
      for (var i = 0; i < presetElements.length; i++) {
        var el = presetElements[i]
        var newEl = self.elementFromBackend(el)
        newEl.id = null
        newElements.push(newEl)
      }

      // Limpar elementos atuais e aplicar os do preset
      self.templateData.elements.splice(0, self.templateData.elements.length)
      for (var j = 0; j < newElements.length; j++) {
        self.templateData.elements.push(newElements[j])
      }

      // Resetar seleção
      self.selectedElement = null

      // Notificar usuário
      self.$bvToast.toast('Modelo "' + preset.name + '" aplicado com sucesso!', {
        title: 'Modelo Aplicado',
        variant: 'success',
        solid: true,
        autoHideDelay: 3000
      })
    },
    loadLabelSizes: function () {
      var self = this
      services.listLabelSizes()
        .then(function (response) {
          self.labelSizes = self.extractArray(response)
          // Extrair impressoras únicas
          var printersSet = {}
          self.labelSizes.forEach(function (size) {
            if (size.printer_model) {
              printersSet[size.printer_model] = true
            }
          })
          self.printers = Object.keys(printersSet)
        })
        .catch(function (error) {
          console.error('Erro ao carregar tamanhos:', error)
        })
    },
    loadRegistrationControlFields: function () {
      var self = this
      services.getAvailableFields()
        .then(function (response) {
          var data = self.extractData(response)
          var rc = (data && data.registration_control) || []
          // O elemento guarda a chave do resolver em standard_field_name.
          self.registrationControlFields = rc.map(function (f) {
            return {
              key: f.key,
              label: f.label,
              field_type: 'registration_control',
              field_source_id: null,
              standard_field_name: f.key
            }
          })
        })
        .catch(function (error) {
          console.error('Erro ao carregar campos de controle de inscrição:', error)
        })
    },
    loadParticipantCardFields: function () {
      var self = this
      self.loadingFields = true

      // Mapeamento de nomes de campos padrão para português
      var standardFieldLabels = {
        name: 'Nome Completo',
        badge_name: 'Nome para Crachá',
        email: 'E-mail',
        cpf: 'CPF',
        phone: 'Telefone',
        gender: 'Gênero',
        birthdate: 'Data de Nascimento',
        company: 'Empresa',
        position: 'Cargo',
        city: 'Cidade',
        state: 'Estado',
        country: 'País',
        address: 'Endereço',
        zip_code: 'CEP',
        rg: 'RG',
        passport: 'Passaporte'
      }

      participantCardServices.getGlobalCard()
        .then(function (response) {
          var card = response.data || response
          var fields = []

          // Extrair campos de todas as tabs da ficha — preservando field_type e
          // field_source_id pra que o renderer do backend resolva na fonte certa.
          // O response vem sem o transformer (json direto do model), então a
          // discriminação é por `field_source` (coluna raw) com fallback pelos
          // campos presentes.
          if (card && card.tabs && Array.isArray(card.tabs)) {
            card.tabs.forEach(function (tab) {
              if (tab.fields && Array.isArray(tab.fields)) {
                tab.fields.forEach(function (field) {
                  var source = field.field_source
                  var ff = field.form_field || null
                  var entry = null

                  // 1. Standard — tem standard_field_name no whitelist do backend
                  if (source === 'standard' || (!source && field.standard_field_name)) {
                    var stdName = field.standard_field_name
                    if (!stdName) return
                    entry = {
                      key: stdName,
                      label: standardFieldLabels[stdName] || field.label || stdName,
                      field_type: 'standard',
                      field_source_id: null,
                      standard_field_name: stdName
                    }
                  // 2. Form field — vem de FormField (Form Builder externo)
                  } else if (source === 'form_field' || (!source && (field.form_field_id || ff))) {
                    var ffId = field.form_field_id || (ff && ff.id)
                    if (!ffId) return
                    entry = {
                      key: (ff && ff.name) || ('form_field_' + ffId),
                      label: field.label || (ff && ff.label) || ('Campo ' + ffId),
                      field_type: 'form_field',
                      field_source_id: ffId,
                      standard_field_name: null
                    }
                  // 3. Internal — campo interno do card builder
                  } else if (source === 'internal') {
                    entry = {
                      key: 'card_field_' + field.id,
                      label: field.label || ('Campo ' + field.id),
                      field_type: 'card_field',
                      field_source_id: field.id,
                      standard_field_name: null
                    }
                  }

                  if (entry) {
                    fields.push(entry)
                  }
                })
              }
            })
          }

          self.participantCardFields = fields
        })
        .catch(function (error) {
          console.error('Erro ao carregar campos da ficha:', error)
        })
        .finally(function () {
          self.loadingFields = false
        })
    },
    loadTemplate: function (id) {
      var self = this
      self.loading = true

      services.getTemplate(id)
        .then(function (response) {
          var responseData = self.extractData(response)

          if (!responseData) {
            errorMessage('Template nao encontrado')
            return
          }
          self.templateData = responseData

          // Tratar elementos que podem vir em formatos diferentes
          var rawElements = []
          if (responseData.elements) {
            if (Array.isArray(responseData.elements)) {
              rawElements = responseData.elements
            } else if (responseData.elements.data) {
              rawElements = responseData.elements.data
            }
          }

          // Converter elementos do formato do backend para o frontend
          self.templateData.elements = rawElements.map(function (el) {
            return self.elementFromBackend(el)
          })

          self.selectedTemplateId = id
          self.selectedPrinter = self.templateData.printer_model || ''
          self.saveHistory()
        })
        .catch(function (error) {
          console.error('Erro ao carregar template:', error)
          errorMessage('Falha ao carregar template')
        })
        .finally(function () {
          self.loading = false
        })
    },

    // Operacoes de Template
    createNewTemplate: function () {
      this.templateData = {
        id: null,
        name: 'Novo Template',
        printer_model: '',
        label_size_id: null,
        orientation: 'landscape',
        width: null,
        height: null,
        background_color: '#FFFFFF',
        background_image: null,
        is_active: true,
        elements: []
      }
      this.selectedTemplateId = null
      this.selectedPrinter = ''
      this.selectedElement = null
      this.history = []
      this.historyIndex = -1
    },
    selectTemplate: function (template) {
      this.loadTemplate(template.id)
    },
    saveTemplate: function () {
      var self = this
      self.saving = true

      if (!self.templateData.name) {
        errorMessage('Nome do template e obrigatorio')
        self.saving = false
        return
      }

      if (!self.templateData.label_size_id) {
        errorMessage('Selecione um tamanho de etiqueta')
        self.saving = false
        return
      }

      // Converter elementos para o formato do backend
      var elementsForBackend = self.templateData.elements.map(function (el) {
        return self.elementToBackend(el)
      })

      var payload = {
        id: self.templateData.id,
        name: self.templateData.name,
        printer_model: self.selectedPrinter,
        label_size_id: self.templateData.label_size_id,
        orientation: self.templateData.orientation,
        width: self.templateData.width,
        height: self.templateData.height,
        background_color: self.templateData.background_color,
        background_image: self.templateData.background_image,
        is_active: self.templateData.is_active,
        elements: elementsForBackend
      }

      services.saveTemplate(payload)
        .then(function (response) {
          var responseData = self.extractData(response)

          if (responseData) {
            // Atualizar ID do template se for novo
            if (!self.templateData.id && responseData.id) {
              self.templateData.id = responseData.id
              self.selectedTemplateId = responseData.id
            }

            // Atualizar elementos com os IDs do banco de dados
            // Isso é crucial para que edições subsequentes atualizem os elementos existentes
            if (responseData.elements) {
              var savedElements = []
              if (Array.isArray(responseData.elements)) {
                savedElements = responseData.elements
              } else if (responseData.elements.data) {
                savedElements = responseData.elements.data
              }

              // Converter elementos do backend para o formato do frontend
              self.templateData.elements = savedElements.map(function (el) {
                return self.elementFromBackend(el)
              })
            }
          }
          self.lastSaved = new Date()
          self.loadTemplates()
          successMessage('Template salvo com sucesso')
        })
        .catch(function (error) {
          console.error('Erro ao salvar template:', error)
          var msg = 'Falha ao salvar template'
          if (error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          } else if (error.message) {
            msg = error.message
          }
          errorMessage(msg)
        })
        .finally(function () {
          self.saving = false
        })
    },
    autoSave: function () {
      // Auto-save ao sair do campo de nome
      if (this.templateData.id && this.templateData.name) {
        this.saveTemplate()
      }
    },
    toggleTemplateMenu: function (template) {
      if (this.activeMenuTemplateId === template.id) {
        this.activeMenuTemplateId = null
      } else {
        this.activeMenuTemplateId = template.id
      }
    },
    closeTemplateMenu: function () {
      this.activeMenuTemplateId = null
    },
    handleClickOutsideMenu: function (event) {
      // Fecha o menu se clicar fora dele
      if (this.activeMenuTemplateId && !event.target.closest('.template-menu-wrapper')) {
        this.activeMenuTemplateId = null
      }
    },
    setTemplateAsDefault: function (template) {
      var self = this
      self.closeTemplateMenu()

      services.setAsDefault(template.id)
        .then(function () {
          // Atualizar lista local
          self.templates.forEach(function (t) {
            t.is_default = t.id === template.id
          })
          self.$toast.success('Template definido como padrão!')
        })
        .catch(function (error) {
          console.error('Erro ao definir como padrão:', error)
          self.$toast.error('Erro ao definir template como padrão')
        })
    },
    duplicateTemplateAction: function (template) {
      var self = this
      self.closeTemplateMenu()

      services.duplicateTemplate(template.id)
        .then(function (response) {
          var newTemplate = response.data.data || response.data
          self.templates.unshift(newTemplate)
          self.$toast.success('Template duplicado com sucesso!')
        })
        .catch(function (error) {
          console.error('Erro ao duplicar:', error)
          self.$toast.error('Erro ao duplicar template')
        })
    },
    deleteTemplateAction: function (template) {
      var self = this
      self.closeTemplateMenu()

      if (!confirm('Tem certeza que deseja excluir este template?')) {
        return
      }

      services.deleteTemplate(template.id)
        .then(function () {
          self.templates = self.templates.filter(function (t) {
            return t.id !== template.id
          })
          if (self.selectedTemplateId === template.id) {
            self.selectedTemplateId = null
            self.resetTemplateData()
          }
          self.$toast.success('Template excluído com sucesso!')
        })
        .catch(function (error) {
          console.error('Erro ao excluir:', error)
          self.$toast.error('Erro ao excluir template')
        })
    },

    // Impressora e Tamanho
    onPrinterChange: function () {
      this.templateData.label_size_id = null
      this.templateData.width = null
      this.templateData.height = null
      this.templateData.printer_model = this.selectedPrinter
    },
    onLabelSizeChange: function () {
      var self = this
      var size = self.labelSizes.find(function (s) {
        return s.id === self.templateData.label_size_id
      })
      if (size) {
        self.templateData.width = size.width
        self.templateData.height = size.height
        self.templateData.orientation = size.orientation || 'landscape'
      }
    },
    setOrientation: function (orientation) {
      // Trocar largura e altura ao mudar orientação
      if (this.templateData.orientation !== orientation) {
        var tempWidth = this.templateData.width
        this.templateData.width = this.templateData.height
        this.templateData.height = tempWidth
        this.templateData.orientation = orientation
      }
    },
    getLabelSizeText: function (template) {
      if (!template.width || !template.height) return ''
      return template.width + 'mm x ' + template.height + 'mm'
    },

    // Operacoes de Elementos
    addElement: function (type, config) {
      var element = {
        id: 'temp_' + Date.now(),
        type: type,
        name: '',
        position_x: config && config.position_x !== undefined ? config.position_x : 20,
        position_y: config && config.position_y !== undefined ? config.position_y : 20,
        width: type === 'line' ? 100 : 80,
        height: type === 'line' ? 2 : (type === 'text' || type === 'dynamic_field' ? 24 : 60),
        z_index: this.templateData.elements.length,
        is_visible: true,
        is_locked: false,
        // Propriedades de texto
        content: type === 'text' ? 'Texto' : '',
        font_family: 'Arial',
        font_size: 12,
        font_color: '#000000',
        font_bold: false,
        font_italic: false,
        font_underline: false,
        text_transform: 'none',
        // Ajuste do texto: 'box' (encolhe pra caber na caixa, padrão atual),
        // 'shrink' (linha única, só reduz fonte se estourar a largura),
        // 'wrap' (mantém a fonte, quebra linha e cresce pra baixo)
        text_fit: 'box',
        text_align: 'left',
        // Campo dinamico
        field_key: config && config.field_key ? config.field_key : null,
        // Propriedades de imagem
        image_url: '',
        object_fit: 'contain',
        // QR/Codigo de barras
        data_source: 'badge_id',
        custom_value: '',
        // Forma
        shape_type: 'rectangle',
        fill_color: '#FFFFFF',
        border_color: '#000000',
        border_width: 1
      }

      if (config) {
        Object.assign(element, config)
      }

      this.templateData.elements.push(element)
      this.selectedElement = element
      this.activeTab = 'properties'
      this.saveHistory()
    },
    addElementAtCenter: function (type) {
      var canvasWidth = (this.templateData.width || 100) * 3.78
      var canvasHeight = (this.templateData.height || 62) * 3.78
      this.addElement(type, {
        position_x: Math.round((canvasWidth / 2 - 40) / this.zoom),
        position_y: Math.round((canvasHeight / 2 - 30) / this.zoom)
      })
    },
    addDynamicFieldAtCenter: function (field) {
      var canvasWidth = (this.templateData.width || 100) * 3.78
      var canvasHeight = (this.templateData.height || 62) * 3.78
      this.addElement('dynamic_field', {
        position_x: Math.round((canvasWidth / 2 - 40) / this.zoom),
        position_y: Math.round((canvasHeight / 2 - 12) / this.zoom),
        field_key: field.key,
        field_type: field.field_type || 'standard',
        field_source_id: field.field_source_id || null,
        standard_field_name: field.standard_field_name || null,
        name: field.label
      })
    },
    selectElement: function (element) {
      this.selectedElement = element
      this.activeTab = 'properties'
    },
    deselectElement: function () {
      this.selectedElement = null
    },
    deleteElement: function () {
      var self = this
      if (!self.selectedElement) return
      var index = self.templateData.elements.findIndex(function (e) {
        return e.id === self.selectedElement.id
      })
      if (index !== -1) {
        self.templateData.elements.splice(index, 1)
        self.selectedElement = null
        self.saveHistory()
      }
    },
    updateElement: function () {
      this.saveHistory()
    },
    toggleVisibility: function (element) {
      element.is_visible = !element.is_visible
    },
    toggleLock: function (element) {
      element.is_locked = !element.is_locked
    },

    // Drag & Drop da paleta
    onElementDragStart: function (event, el) {
      event.dataTransfer.setData('application/json', JSON.stringify({ type: 'element', data: el }))
      event.dataTransfer.effectAllowed = 'copy'
    },
    onFieldDragStart: function (event, field) {
      event.dataTransfer.setData('application/json', JSON.stringify({ type: 'field', data: field }))
      event.dataTransfer.effectAllowed = 'copy'
    },
    onDragOver: function (event) {
      event.preventDefault()
    },
    onDrop: function (event) {
      event.preventDefault()
      var data = event.dataTransfer.getData('application/json')
      if (!data) return

      try {
        var parsed = JSON.parse(data)
        var rect = this.$refs.badgeCanvas.getBoundingClientRect()
        var x = (event.clientX - rect.left) / this.zoom
        var y = (event.clientY - rect.top) / this.zoom

        if (parsed.type === 'element') {
          this.addElement(parsed.data.type, { position_x: x, position_y: y })
        } else if (parsed.type === 'field') {
          this.addElement('dynamic_field', {
            position_x: x,
            position_y: y,
            field_key: parsed.data.key,
            field_type: parsed.data.field_type || 'standard',
            field_source_id: parsed.data.field_source_id || null,
            standard_field_name: parsed.data.standard_field_name || null,
            name: parsed.data.label
          })
        }
      } catch (e) {
        console.error('Dados de drop invalidos', e)
      }
    },

    // Arrasto de elementos no canvas
    startDrag: function (event, element) {
      if (element.is_locked) return
      this.isDragging = true
      this.selectedElement = element
      this.dragStart = {
        x: event.clientX - element.position_x * this.zoom,
        y: event.clientY - element.position_y * this.zoom
      }
    },
    onMouseMove: function (event) {
      if (this.isDragging && this.selectedElement && !this.selectedElement.is_locked) {
        var x = (event.clientX - this.dragStart.x) / this.zoom
        var y = (event.clientY - this.dragStart.y) / this.zoom

        this.selectedElement.position_x = Math.max(0, x)
        this.selectedElement.position_y = Math.max(0, y)
      }

      if (this.isResizing && this.selectedElement) {
        this.handleResize(event)
      }
    },
    onMouseUp: function () {
      if (this.isDragging || this.isResizing) {
        this.saveHistory()
      }
      this.isDragging = false
      this.isResizing = false
    },

    // Redimensionamento
    startResize: function (event, element, handle) {
      this.isResizing = true
      this.resizeHandle = handle
      this.resizeStart = {
        x: event.clientX,
        y: event.clientY,
        width: element.width,
        height: element.height,
        posX: element.position_x,
        posY: element.position_y
      }
    },
    handleResize: function (event) {
      if (!this.selectedElement || !this.resizeStart) return

      var dx = (event.clientX - this.resizeStart.x) / this.zoom
      var dy = (event.clientY - this.resizeStart.y) / this.zoom

      switch (this.resizeHandle) {
        case 'se':
          this.selectedElement.width = Math.max(20, this.resizeStart.width + dx)
          this.selectedElement.height = Math.max(20, this.resizeStart.height + dy)
          break
        case 'sw':
          this.selectedElement.width = Math.max(20, this.resizeStart.width - dx)
          this.selectedElement.position_x = this.resizeStart.posX + dx
          this.selectedElement.height = Math.max(20, this.resizeStart.height + dy)
          break
        case 'ne':
          this.selectedElement.width = Math.max(20, this.resizeStart.width + dx)
          this.selectedElement.height = Math.max(20, this.resizeStart.height - dy)
          this.selectedElement.position_y = this.resizeStart.posY + dy
          break
        case 'nw':
          this.selectedElement.width = Math.max(20, this.resizeStart.width - dx)
          this.selectedElement.height = Math.max(20, this.resizeStart.height - dy)
          this.selectedElement.position_x = this.resizeStart.posX + dx
          this.selectedElement.position_y = this.resizeStart.posY + dy
          break
      }
    },

    // Camadas
    onLayersReorder: function () {
      this.templateData.elements = this.templateData.elements.map(function (el, index) {
        el.z_index = index
        return el
      })
      this.saveHistory()
    },

    // Zoom
    zoomIn: function () {
      this.zoom = Math.min(2, this.zoom + 0.1)
    },
    zoomOut: function () {
      this.zoom = Math.max(0.5, this.zoom - 0.1)
    },
    resetZoom: function () {
      this.zoom = 1
    },

    // Helpers para QRCode e Foto do Participante (sempre retorna null no builder - usa placeholders)
    getParticipantQrCodeUrl: function () {
      return null
    },
    getParticipantPhotoUrl: function () {
      return null
    },

    // Historico (Desfazer/Refazer)
    saveHistory: function () {
      var snapshot = JSON.stringify(this.templateData.elements)
      this.history = this.history.slice(0, this.historyIndex + 1)
      this.history.push(snapshot)
      this.historyIndex = this.history.length - 1
      this.updateHistoryState()
    },
    undo: function () {
      if (this.historyIndex > 0) {
        this.historyIndex--
        this.templateData.elements = JSON.parse(this.history[this.historyIndex])
        this.updateHistoryState()
      }
    },
    redo: function () {
      if (this.historyIndex < this.history.length - 1) {
        this.historyIndex++
        this.templateData.elements = JSON.parse(this.history[this.historyIndex])
        this.updateHistoryState()
      }
    },
    updateHistoryState: function () {
      this.canUndo = this.historyIndex > 0
      this.canRedo = this.historyIndex < this.history.length - 1
    },

    // Atalhos de teclado
    onKeyDown: function (event) {
      if (event.key === 'Delete' && this.selectedElement) {
        this.deleteElement()
      }
      if (event.ctrlKey && event.key === 'z') {
        event.preventDefault()
        this.undo()
      }
      if (event.ctrlKey && event.key === 'y') {
        event.preventDefault()
        this.redo()
      }
    },

    // Exportar e Imprimir
    exportTemplate: function () {
      successMessage('Funcionalidade de exportacao em breve')
    },

    /**
     * Teste de Impressão - Abre nova aba com o badge no tamanho exato da etiqueta
     * Usa CSS @page para definir tamanho preciso em mm
     * Referências: MDN @page, CSS for printing best practices
     */
    printTest: function () {
      // Validações
      if (!this.templateData.width || !this.templateData.height) {
        errorMessage('Configure o tamanho da etiqueta antes de testar a impressão')
        return
      }

      // Abre nova aba (mais confiável que popup)
      var printWindow = window.open('', '_blank')

      if (!printWindow) {
        errorMessage('Não foi possível abrir a janela de impressão. Verifique se popups estão permitidos.')
        return
      }

      // Gera o HTML completo do badge
      var html = this.generateBadgePrintDocument()

      // Escreve o documento
      printWindow.document.open()
      printWindow.document.write(html)
      printWindow.document.close()
    },

    /**
     * Gera documento HTML completo para impressão do badge
     * Usa medidas em mm para precisão na impressão
     */
    generateBadgePrintDocument: function () {
      var width = this.templateData.width
      var height = this.templateData.height
      var bgColor = this.templateData.background_color || '#FFFFFF'
      var bgImage = this.templateData.background_image
      var templateName = this.templateData.name || 'Badge'
      var participantName = 'Preview'

      // Gera HTML dos elementos - passa dimensões da etiqueta para calcular espaço disponível
      var elementsHtml = this.generateElementsHtml(width, height)

      // Monta o documento HTML
      var html = [
        '<!DOCTYPE html>',
        '<html lang="pt-BR">',
        '<head>',
        '  <meta charset="UTF-8">',
        '  <meta name="viewport" content="width=device-width, initial-scale=1.0">',
        '  <title>Impressão - ' + this.escapeHtml(templateName) + ' - ' + this.escapeHtml(participantName) + '</title>',
        '  <style>',
        '    /* Reset e configuração base */',
        '    * { margin: 0; padding: 0; box-sizing: border-box; }',
        '',
        '    /* Configuração da página para impressão */',
        '    @page {',
        '      size: ' + width + 'mm ' + height + 'mm;',
        '      margin: 0;',
        '    }',
        '',
        '    /* Estilos para visualização na tela */',
        '    body {',
        '      font-family: Arial, Helvetica, sans-serif;',
        '      background: #e9ecef;',
        '      min-height: 100vh;',
        '      display: flex;',
        '      flex-direction: column;',
        '      align-items: center;',
        '      padding: 30px;',
        '    }',
        '',
        '    .print-header {',
        '      text-align: center;',
        '      margin-bottom: 20px;',
        '    }',
        '',
        '    .print-header h1 {',
        '      font-size: 18px;',
        '      color: #495057;',
        '      margin-bottom: 5px;',
        '    }',
        '',
        '    .print-header p {',
        '      font-size: 13px;',
        '      color: #6c757d;',
        '    }',
        '',
        '    .print-actions {',
        '      display: flex;',
        '      gap: 10px;',
        '      margin-bottom: 25px;',
        '    }',
        '',
        '    .btn {',
        '      display: inline-flex;',
        '      align-items: center;',
        '      gap: 6px;',
        '      padding: 10px 20px;',
        '      border: none;',
        '      border-radius: 6px;',
        '      font-size: 14px;',
        '      font-weight: 500;',
        '      cursor: pointer;',
        '      transition: all 0.2s;',
        '    }',
        '',
        '    .btn-primary {',
        '      background: #556ee6;',
        '      color: white;',
        '    }',
        '',
        '    .btn-primary:hover {',
        '      background: #4458cb;',
        '    }',
        '',
        '    .btn-secondary {',
        '      background: #74788d;',
        '      color: white;',
        '    }',
        '',
        '    .btn-secondary:hover {',
        '      background: #5d6071;',
        '    }',
        '',
        '    .badge-container {',
        '      background: white;',
        '      padding: 15px;',
        '      border-radius: 8px;',
        '      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);',
        '    }',
        '',
        '    .badge {',
        '      position: relative;',
        '      width: ' + width + 'mm;',
        '      height: ' + height + 'mm;',
        '      background-color: ' + bgColor + ';',
        bgImage ? '      background-image: url(\'' + bgImage + '\');' : '',
        bgImage ? '      background-size: cover;' : '',
        bgImage ? '      background-position: center;' : '',
        '      overflow: hidden;',
        '      border: 1px dashed #dee2e6;',
        '    }',
        '',
        '    .print-info {',
        '      margin-top: 15px;',
        '      font-size: 12px;',
        '      color: #868e96;',
        '      text-align: center;',
        '    }',
        '',
        '    /* Estilos para impressão */',
        '    @media print {',
        '      body {',
        '        background: white;',
        '        padding: 0;',
        '        min-height: auto;',
        '      }',
        '',
        '      .no-print {',
        '        display: none !important;',
        '      }',
        '',
        '      .badge-container {',
        '        padding: 0;',
        '        box-shadow: none;',
        '        border-radius: 0;',
        '      }',
        '',
        '      .badge {',
        '        border: none;',
        '      }',
        '    }',
        '',
        '    /* Estilos dos elementos do badge */',
        '    .badge-element {',
        '      position: absolute;',
        '      overflow: hidden;',
        '    }',
        '',
        '    .element-text {',
        '      display: flex;',
        '      align-items: center;',
        '      white-space: nowrap;',
        '    }',
        '',
        '    .element-image img {',
        '      width: 100%;',
        '      height: 100%;',
        '      object-fit: contain;',
        '    }',
        '',
        '    .element-photo img {',
        '      width: 100%;',
        '      height: 100%;',
        '    }',
        '',
        '    .element-qrcode img {',
        '      width: 100%;',
        '      height: 100%;',
        '      object-fit: contain;',
        '    }',
        '',
        '    .qrcode-placeholder {',
        '      width: 100%;',
        '      height: 100%;',
        '      display: flex;',
        '      flex-direction: column;',
        '      align-items: center;',
        '      justify-content: center;',
        '      background: #f8f9fa;',
        '      color: #adb5bd;',
        '      font-size: 10px;',
        '    }',
        '',
        '    .photo-placeholder {',
        '      width: 100%;',
        '      height: 100%;',
        '      display: flex;',
        '      flex-direction: column;',
        '      align-items: center;',
        '      justify-content: center;',
        '      background: #f8f9fa;',
        '      color: #adb5bd;',
        '      font-size: 10px;',
        '    }',
        '  </style>',
        '</head>',
        '<body>',
        '  <div class="print-header no-print">',
        '    <h1>Teste de Impressão</h1>',
        '    <p>' + this.escapeHtml(templateName) + ' - ' + this.escapeHtml(participantName) + '</p>',
        '  </div>',
        '',
        '  <div class="print-actions no-print">',
        '    <button class="btn btn-primary" onclick="window.print()">',
        '      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">',
        '        <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>',
        '        <rect x="6" y="14" width="12" height="8"/>',
        '      </svg>',
        '      Imprimir',
        '    </button>',
        '    <button class="btn btn-secondary" onclick="window.close()">',
        '      Fechar',
        '    </button>',
        '  </div>',
        '',
        '  <div class="badge-container">',
        '    <div class="badge">',
        elementsHtml,
        '    </div>',
        '  </div>',
        '',
        '  <div class="print-info no-print">',
        '    Tamanho da etiqueta: ' + width + 'mm × ' + height + 'mm',
        '    ' + (this.selectedPrinter ? ' | Impressora: ' + this.escapeHtml(this.selectedPrinter) : ''),
        '  </div>',
        '</body>',
        '</html>'
      ].filter(function (line) { return line !== '' }).join('\n')

      return html
    },

    /**
     * Gera HTML de todos os elementos visíveis do badge
     */
    generateElementsHtml: function (labelWidthMm, labelHeightMm) {
      var self = this
      var html = []

      this.templateData.elements.forEach(function (element) {
        if (element.is_visible === false) return

        var elementHtml = self.generateSingleElementHtml(element, labelWidthMm, labelHeightMm)
        if (elementHtml) {
          html.push(elementHtml)
        }
      })

      return html.join('\n')
    },

    /**
     * Gera HTML de um único elemento
     * Elementos de texto usam espaço disponível até a borda da etiqueta
     */
    generateSingleElementHtml: function (element, labelWidthMm, labelHeightMm) {
      var PX_TO_MM = 1 / 3.78
      var MARGIN_MM = 2 // Margem de segurança

      // Reflow: elementos em modo 'wrap' que cresceram empurram este pra baixo.
      var reflowOffset = this.textReflowOffsets()[element.id] || 0

      var leftMm = (element.position_x || 0) * PX_TO_MM
      var topMm = ((element.position_y || 0) + reflowOffset) * PX_TO_MM
      var elementWidthMm = (element.width || 20) * PX_TO_MM
      // No modo 'wrap' a caixa cresce pra baixo; nos demais mantém a altura.
      var elementHeightMm = this.elementRenderedHeight(element) * PX_TO_MM

      // Calcular espaço disponível na LARGURA até a borda da etiqueta
      // Altura mantém o tamanho original do elemento para não sobrepor outros
      // Linha única / caixa podem usar até a borda da etiqueta; 'wrap' respeita a
      // largura desenhada (é ela que define onde o texto quebra).
      var availableWidthMm = element.text_fit === 'wrap'
        ? elementWidthMm
        : Math.max(elementWidthMm, labelWidthMm - leftMm - MARGIN_MM)

      // Estilos base do elemento (posição em mm)
      var baseStyles = [
        'position: absolute',
        'left: ' + leftMm.toFixed(2) + 'mm',
        'top: ' + topMm.toFixed(2) + 'mm',
        'z-index: ' + (element.z_index || 1)
      ]

      if (element.rotation) {
        baseStyles.push('transform: rotate(' + element.rotation + 'deg)')
      }

      var content = ''
      var additionalStyles = []
      var elementClass = 'badge-element'

      switch (element.type) {
        case 'text':
          elementClass += ' element-text'
          content = element.content || ''
          additionalStyles = this.getTextStyles(element, content, availableWidthMm.toFixed(2), elementHeightMm.toFixed(2))
          content = this.escapeHtml(content)
          baseStyles.push('width: ' + availableWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'dynamic_field':
          elementClass += ' element-text'
          content = this.getFieldPreviewValue(element)

          // Se for campo de nome (name ou badge_name), aplicar lógica inteligente
          if (this.isNameField(element) && content) {
            content = this.getSmartNameValue(element, content, availableWidthMm, elementHeightMm)
          }

          additionalStyles = this.getTextStyles(element, content, availableWidthMm.toFixed(2), elementHeightMm.toFixed(2))
          content = this.escapeHtml(content)
          baseStyles.push('width: ' + availableWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'image':
          elementClass += ' element-image'
          if (element.image_url) {
            var objectFit = element.object_fit || 'contain'
            content = '<img src="' + element.image_url + '" style="object-fit: ' + objectFit + ';" />'
          }
          baseStyles.push('width: ' + elementWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'photo':
          elementClass += ' element-photo'
          var photoUrl = this.getParticipantPhotoUrl()
          if (photoUrl) {
            var photoFit = element.object_fit || 'cover'
            content = '<img src="' + photoUrl + '" style="object-fit: ' + photoFit + ';" />'
          } else {
            content = '<div class="photo-placeholder"><span>Sem foto</span></div>'
          }
          baseStyles.push('width: ' + elementWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'qrcode':
          elementClass += ' element-qrcode'
          content = '<img src="' + this.qrcodePreviewImage + '" style="width: 100%; height: 100%; object-fit: contain;" />'
          baseStyles.push('width: ' + elementWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'shape':
          additionalStyles = this.getShapePrintStyles(element)
          content = ''
          baseStyles.push('width: ' + elementWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'line':
          additionalStyles.push('background-color: ' + (element.border_color || '#000000'))
          baseStyles.push('width: ' + elementWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + (element.line_width || 1) + 'px')
          break

        default:
          return ''
      }

      var allStyles = baseStyles.concat(additionalStyles).join('; ')

      return '<div class="' + elementClass + '" style="' + allStyles + '">' + content + '</div>'
    },

    /**
     * Gera estilos CSS para elementos de texto
     * Usa medição real do DOM para encontrar tamanho ótimo (abordagem P-Touch/textFit)
     */
    getTextStyles: function (element, content, elementWidthMm, elementHeightMm) {
      var PX_TO_MM = 1 / 3.78
      var fontSizePx = element.font_size || 12
      var widthMm = parseFloat(elementWidthMm)
      var heightMm = parseFloat(elementHeightMm)
      var fontFamily = element.font_family || 'Arial, sans-serif'
      var fontWeight = element.font_bold ? 'bold' : 'normal'
      var textAlign = element.text_align || 'left'
      var fit = element.text_fit || 'box'

      // Maiúscula é mais larga; mede o texto já transformado.
      var measureContent = element.text_transform === 'uppercase'
        ? String(content).toUpperCase()
        : content

      var alignItems = 'flex-start'
      if (textAlign === 'center') alignItems = 'center'
      else if (textAlign === 'right') alignItems = 'flex-end'

      var base = [
        'display: flex',
        'flex-direction: column',
        'font-family: ' + fontFamily,
        'color: ' + (element.font_color || '#000000'),
        'font-weight: ' + fontWeight,
        'font-style: ' + (element.font_italic ? 'italic' : 'normal'),
        'text-decoration: ' + (element.font_underline ? 'underline' : 'none'),
        'text-transform: ' + (element.text_transform || 'none'),
        'text-align: ' + textAlign,
        'align-items: ' + alignItems
      ]

      // WRAP: mantém a fonte e quebra na largura da caixa. Precisa ser BLOCO —
      // num flex o texto vira item dimensionado pelo conteúdo e não quebra.
      if (fit === 'wrap') {
        return [
          'display: block',
          'width: 100%',
          'font-family: ' + fontFamily,
          'font-size: ' + (fontSizePx * PX_TO_MM).toFixed(2) + 'mm',
          'line-height: 1.2',
          'color: ' + (element.font_color || '#000000'),
          'font-weight: ' + fontWeight,
          'font-style: ' + (element.font_italic ? 'italic' : 'normal'),
          'text-decoration: ' + (element.font_underline ? 'underline' : 'none'),
          'text-transform: ' + (element.text_transform || 'none'),
          'text-align: ' + textAlign,
          'white-space: normal',
          'overflow-wrap: break-word',
          'word-wrap: break-word'
        ]
      }

      // SHRINK: linha única, só reduz a fonte se estourar a largura. Nunca quebra.
      if (fit === 'shrink') {
        var px = fontSizePx
        var maxPx = widthMm / PX_TO_MM
        var guardShrink = 0
        while (px > 4 && this.measureTextWidthPx(measureContent, px, fontFamily, fontWeight) > maxPx && guardShrink < 200) {
          px -= 0.5
          guardShrink++
        }
        base.push('font-size: ' + (px * PX_TO_MM).toFixed(2) + 'mm')
        base.push('line-height: 1.2')
        base.push('white-space: nowrap')
        base.push('justify-content: center')
        base.push('overflow: hidden')
        base.push('text-overflow: ellipsis')
        return base
      }

      // BOX (padrão): ajusta à caixa — quebra e/ou reduz a fonte pra caber.
      var fitResult = this.findOptimalFontSize(measureContent, fontSizePx, widthMm, heightMm, fontFamily, fontWeight)
      base.push('font-size: ' + fitResult.fontSize + 'mm')
      base.push('line-height: ' + fitResult.lineHeight)
      base.push('overflow: hidden')
      if (fitResult.shouldWrap) {
        base.push('white-space: normal')
        base.push('word-wrap: break-word')
        base.push('justify-content: center')
      } else {
        base.push('white-space: nowrap')
        base.push('justify-content: center')
        base.push('text-overflow: ellipsis')
      }
      return base
    },

    /**
     * Encontra o tamanho de fonte ótimo usando medição real do DOM
     * Abordagem inspirada no textFit: https://github.com/STRML/textFit
     *
     * Estratégia:
     * 1. Tentar fonte original em linha única
     * 2. Se não couber, tentar fonte original com wrap (quebra de linha)
     * 3. Redução de fonte comentada por enquanto
     */
    findOptimalFontSize: function (content, baseFontSizePx, widthMm, heightMm, fontFamily, fontWeight) {
      var PX_TO_MM = 1 / 3.78
      var MM_TO_PX = 3.78
      var baseFontSizeMm = baseFontSizePx * PX_TO_MM
      var LINE_HEIGHT = 1.2

      var result = {
        fontSize: baseFontSizeMm.toFixed(2),
        shouldWrap: false,
        lineHeight: LINE_HEIGHT.toString()
      }

      if (!content || content.length === 0) {
        return result
      }

      // Converter dimensões para pixels para medição
      var containerWidthPx = widthMm * MM_TO_PX
      var containerHeightPx = heightMm * MM_TO_PX

      // Criar elemento de medição invisível
      var measureEl = document.createElement('div')
      measureEl.style.cssText = [
        'position: absolute',
        'visibility: hidden',
        'left: -9999px',
        'top: -9999px',
        'font-family: ' + fontFamily,
        'font-weight: ' + fontWeight,
        'line-height: ' + LINE_HEIGHT,
        'padding: 0',
        'margin: 0',
        'border: 0'
      ].join(';')
      document.body.appendChild(measureEl)

      try {
        // PASSO 1: Testar fonte original em linha única
        measureEl.style.whiteSpace = 'nowrap'
        measureEl.style.width = 'auto'
        measureEl.style.fontSize = baseFontSizePx + 'px'
        measureEl.textContent = content

        var singleLineWidth = measureEl.scrollWidth
        var singleLineHeight = measureEl.scrollHeight

        // Se cabe em linha única com fonte original - perfeito!
        if (singleLineWidth <= containerWidthPx && singleLineHeight <= containerHeightPx) {
          return result
        }

        // PASSO 2: Testar fonte original COM wrap (quebra de linha)
        measureEl.style.whiteSpace = 'normal'
        measureEl.style.wordWrap = 'break-word'
        measureEl.style.width = containerWidthPx + 'px'

        var wrappedHeight = measureEl.scrollHeight

        // Se cabe com wrap na fonte original - usar wrap!
        if (wrappedHeight <= containerHeightPx) {
          result.shouldWrap = true
          return result
        }

        // PASSO 3: Binary search para encontrar maior fonte que cabe com wrap
        var MIN_FONT_SIZE_MM = 2
        var low = MIN_FONT_SIZE_MM * MM_TO_PX
        var high = baseFontSizePx
        var bestFit = low
        var maxIterations = 10

        for (var i = 0; i < maxIterations && low <= high; i++) {
          var mid = Math.floor((low + high) / 2)
          measureEl.style.fontSize = mid + 'px'
          var testHeight = measureEl.scrollHeight

          if (testHeight <= containerHeightPx) {
            bestFit = mid
            low = mid + 1
          } else {
            high = mid - 1
          }
        }

        result.fontSize = (bestFit * PX_TO_MM).toFixed(2)
        result.shouldWrap = true
        return result
      } finally {
        document.body.removeChild(measureEl)
      }
    },

    /**
     * Gera estilos CSS para formas
     */
    getShapePrintStyles: function (element) {
      var styles = [
        'background-color: ' + (element.background_color || 'transparent')
      ]

      if (element.border_width && element.border_width > 0) {
        styles.push('border: ' + element.border_width + 'px solid ' + (element.border_color || '#000000'))
      }

      // Tipo de forma
      var shapeType = element.shape_type || 'rectangle'
      if (shapeType === 'circle' || shapeType === 'ellipse') {
        styles.push('border-radius: 50%')
      } else if (element.border_radius) {
        styles.push('border-radius: ' + element.border_radius + 'px')
      }

      return styles
    },

    /**
     * Escapa HTML para evitar XSS
     */
    escapeHtml: function (text) {
      if (text === null || text === undefined) return ''
      var str = String(text)
      var div = document.createElement('div')
      div.textContent = str
      return div.innerHTML
    },

    /**
     * Verifica se o elemento é um campo de nome (name ou badge_name)
     */
    isNameField: function (element) {
      if (element.type !== 'dynamic_field') return false
      var fieldKey = element.field_key || element.standard_field_name || ''
      return fieldKey === 'name' || fieldKey === 'badge_name'
    },

    /**
     * Extrai primeiro e último nome de um nome completo
     * "Maria Clara dos Santos Silva" -> "Maria Silva"
     * "João Pedro" -> "João Pedro"
     * "Ana" -> "Ana"
     */
    getFirstAndLastName: function (fullName) {
      if (!fullName || typeof fullName !== 'string') return fullName || ''

      var parts = fullName.trim().split(/\s+/)

      if (parts.length <= 2) {
        return fullName.trim()
      }

      var firstName = parts[0]
      var lastName = parts[parts.length - 1]

      return firstName + ' ' + lastName
    },

    /**
     * Mede se o texto cabe no container usando medição real do DOM
     * Retorna true se couber, false se não couber
     */
    textFitsInContainer: function (text, fontSizePx, containerWidthPx, containerHeightPx, fontFamily, fontWeight) {
      var measureEl = document.createElement('div')
      measureEl.style.cssText = [
        'position: absolute',
        'visibility: hidden',
        'left: -9999px',
        'top: -9999px',
        'font-family: ' + fontFamily,
        'font-weight: ' + fontWeight,
        'font-size: ' + fontSizePx + 'px',
        'line-height: 1.2',
        'white-space: nowrap',
        'padding: 0',
        'margin: 0',
        'border: 0'
      ].join(';')
      measureEl.textContent = text
      document.body.appendChild(measureEl)

      var fits = measureEl.scrollWidth <= containerWidthPx && measureEl.scrollHeight <= containerHeightPx
      document.body.removeChild(measureEl)

      return fits
    },

    /**
     * Processa nome inteligente: se nome é longo, usa primeiro+último
     * Regra simples: se tem mais de 2 palavras E mais de 20 caracteres, encurta
     */
    getSmartNameValue: function (element, originalValue, widthMm, heightMm) {
      if (!originalValue || typeof originalValue !== 'string') {
        return originalValue || ''
      }

      var parts = originalValue.trim().split(/\s+/)
      var MAX_CHARS = 20 // Limite de caracteres para nome completo

      // Se tem mais de 2 palavras E é muito longo, encurta
      if (parts.length > 2 && originalValue.length > MAX_CHARS) {
        return this.getFirstAndLastName(originalValue)
      }

      return originalValue
    },

    // Texto exibido de um elemento (preview de campo dinâmico ou conteúdo fixo),
    // já com a transformação de maiúscula aplicada — é o que se mede/renderiza.
    getElementDisplayText: function (element) {
      var text = element.type === 'dynamic_field'
        ? this.getFieldPreviewValue(element)
        : (element.content || '')
      if (element.text_transform === 'uppercase') {
        text = String(text).toUpperCase()
      }
      return text
    },
    // Mede largura de texto num canvas offscreen — determinístico e sem thrash
    // de layout (mesmo algoritmo usado na impressão e espelhado no thumbnail).
    measureTextWidthPx: function (text, fontPx, fontFamily, fontWeight) {
      if (!this._measureCtx) {
        var c = document.createElement('canvas')
        this._measureCtx = c.getContext('2d')
      }
      this._measureCtx.font = (fontWeight || 'normal') + ' ' + fontPx + 'px ' + (fontFamily || 'Arial')
      return this._measureCtx.measureText(text || '').width
    },
    // Quantas linhas o texto ocupa ao quebrar por palavra dentro de maxWidthPx.
    countWrappedLines: function (text, fontPx, fontFamily, fontWeight, maxWidthPx) {
      text = String(text || '').trim()
      if (!text) return 1
      var self = this
      var words = text.split(/\s+/)
      var lines = 1
      var current = ''
      words.forEach(function (word) {
        var candidate = current ? current + ' ' + word : word
        if (self.measureTextWidthPx(candidate, fontPx, fontFamily, fontWeight) <= maxWidthPx || !current) {
          current = candidate
        } else {
          lines++
          current = word
        }
      })
      return lines
    },
    // Altura renderizada do elemento (em unidades do canvas). Só o modo 'wrap'
    // pode crescer além da altura desenhada; os demais mantêm a caixa.
    elementRenderedHeight: function (element) {
      if (element.type !== 'text' && element.type !== 'dynamic_field') {
        return element.height
      }
      if (element.text_fit !== 'wrap') {
        return element.height
      }
      var fontPx = element.font_size || 12
      var lines = this.countWrappedLines(
        this.getElementDisplayText(element), fontPx,
        element.font_family || 'Arial',
        element.font_bold ? 'bold' : 'normal',
        element.width
      )
      var lineHeight = 1.2
      var needed = lines * fontPx * lineHeight
      return Math.max(element.height, needed)
    },
    // Mapa de deslocamento vertical por elemento: um elemento em modo 'wrap' que
    // cresce empurra para baixo TODOS os que começam abaixo da sua base original,
    // para nunca haver texto sobre texto. Usa as posições de projeto (estáveis).
    textReflowOffsets: function () {
      var offsets = {}
      var els = this.templateData.elements
      els.forEach(function (e) { offsets[e.id] = 0 })

      var self = this
      els.forEach(function (g) {
        var overflow = self.elementRenderedHeight(g) - g.height
        if (overflow <= 0) return
        var gBottom = g.position_y + g.height
        els.forEach(function (e) {
          if (e.id === g.id) return
          if (e.position_y >= gBottom) {
            offsets[e.id] += overflow
          }
        })
      })
      return offsets
    },
    // Helpers
    getElementStyle: function (element) {
      var offset = this.textReflowOffsets()[element.id] || 0
      var renderedHeight = this.elementRenderedHeight(element)
      return {
        position: 'absolute',
        left: (element.position_x * this.zoom) + 'px',
        top: ((element.position_y + offset) * this.zoom) + 'px',
        width: (element.width * this.zoom) + 'px',
        height: (renderedHeight * this.zoom) + 'px',
        zIndex: element.z_index,
        opacity: element.is_visible ? 1 : 0.3,
        pointerEvents: element.is_locked ? 'none' : 'auto'
      }
    },
    getElementContentStyle: function (element) {
      var style = {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: element.text_align === 'center' ? 'center' : (element.text_align === 'right' ? 'flex-end' : 'flex-start'),
        overflow: 'hidden'
      }

      if (element.type === 'text' || element.type === 'dynamic_field') {
        style.fontFamily = element.font_family || 'Arial'
        style.fontSize = ((element.font_size || 12) * this.zoom) + 'px'
        style.color = element.font_color || '#000000'
        style.fontWeight = element.font_bold ? 'bold' : 'normal'
        style.fontStyle = element.font_italic ? 'italic' : 'normal'
        style.textDecoration = element.font_underline ? 'underline' : 'none'
        style.textTransform = element.text_transform || 'none'
        style.textAlign = element.text_align || 'left'

        if (element.text_fit === 'wrap') {
          // Bloco (não flex): num flex o texto não quebra na largura da caixa.
          // Ancora no topo e cresce pra baixo (a altura vem de getElementStyle).
          style.display = 'block'
          style.whiteSpace = 'normal'
          style.overflowWrap = 'break-word'
          style.wordWrap = 'break-word'
        } else {
          style.whiteSpace = 'nowrap'
        }
      }

      return style
    },
    getShapeStyle: function (element) {
      var style = {
        width: '100%',
        height: '100%',
        backgroundColor: element.fill_color || '#FFFFFF',
        border: '1px solid ' + (element.border_color || '#000000')
      }
      if (element.shape_type === 'circle') {
        style.borderRadius = '50%'
      } else if (element.shape_type === 'rounded') {
        style.borderRadius = '8px'
      }
      return style
    },
    getFieldPreviewValue: function (element) {
      if (!element.field_key) return '{{campo}}'

      // Placeholders fixos para cada tipo de campo
      var placeholders = {
        name: 'Nome Completo',
        email: 'email@exemplo.com',
        phone: '(99) 00000-0000',
        whatsapp: '(99) 00000-0000',
        document: '000.000.000-00',
        company: 'Empresa',
        position: 'Cargo',
        group_name: 'Grupo',
        badge_id: '000001',
        confirmation_code: 'ABC123',
        category: 'Categoria',
        city: 'Cidade',
        state: 'Estado'
      }

      // Se tem placeholder específico, usa ele
      if (placeholders[element.field_key]) {
        return placeholders[element.field_key]
      }

      // Para campos personalizados, retornar o label do campo
      var field = this.dynamicFields.find(function (f) {
        return f.key === element.field_key
      })
      return field ? field.label : element.field_key
    },
    getElementIcon: function (type) {
      var icons = {
        text: 'bx bx-text',
        dynamic_field: 'bx bx-code-alt',
        image: 'bx bx-image',
        qrcode: 'bx bx-qr',
        barcode: 'bx bx-barcode',
        photo: 'bx bx-user',
        logo: 'bx bx-building',
        shape: 'bx bx-square',
        line: 'bx bx-minus'
      }
      return icons[type] || 'bx bx-square'
    },
    getFieldTypeIcon: function (fieldTypeId) {
      // Mapeamento de tipos de campo para icones
      var iconMap = {
        1: 'bx bx-text', // text
        2: 'bx bx-envelope', // email
        3: 'bx bx-phone', // phone
        4: 'bx bx-list-ul', // select
        5: 'bx bx-check-square', // checkbox
        6: 'bx bx-radio-circle', // radio
        7: 'bx bx-calendar', // date
        8: 'bx bx-time', // time
        9: 'bx bx-hash' // number
      }
      return iconMap[fieldTypeId] || 'bx bx-text'
    },
    getElementLabel: function (element) {
      if (element.name) return element.name
      if (element.type === 'dynamic_field' && element.field_key) {
        var field = this.dynamicFields.find(function (f) {
          return f.key === element.field_key
        })
        return field ? field.label : element.field_key
      }
      if (element.type === 'text') {
        return element.content || 'Texto'
      }
      var type = this.elementTypes.find(function (t) {
        return t.type === element.type
      })
      return type ? type.label : element.type
    },
    getPreviewStyle: function (template) {
      return {
        backgroundColor: template.background_color || '#f8f9fa',
        aspectRatio: (template.width || 100) + ' / ' + (template.height || 62)
      }
    },
    formatTimeAgo: function (date) {
      if (!date) return ''
      var now = new Date()
      var then = new Date(date)
      var diff = Math.floor((now - then) / 1000)

      if (diff < 60) return 'agora'
      if (diff < 3600) return Math.floor(diff / 60) + ' min atras'
      if (diff < 86400) return Math.floor(diff / 3600) + 'h atras'
      if (diff < 604800) return Math.floor(diff / 86400) + ' dias atras'
      return Math.floor(diff / 604800) + ' sem atras'
    }
  }
}
</script>

<style lang="scss" scoped>
.badge-builder-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f6fa;
  overflow: hidden;
}

/* Header */
.builder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
  gap: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.btn-back {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;

  &:hover {
    border-color: #556ee6;
    color: #556ee6;
  }
}

.template-title-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.template-title-input {
  border: none;
  background: transparent;
  font-size: 16px;
  font-weight: 600;
  color: #344054;
  padding: 4px 0;
  width: 200px;

  &:focus {
    outline: none;
    border-bottom: 2px solid #556ee6;
  }
}

.template-status {
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;

  &.active {
    background: #d1fae5;
    color: #059669;
  }

  &.inactive {
    background: #fee2e2;
    color: #dc2626;
  }
}

.header-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex: 1;
}

.config-group {
  display: flex;
  align-items: center;
  gap: 6px;

  label {
    display: flex;
    align-items: center;
    font-size: 11px;
    font-weight: 500;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    height: 28px;
    margin: 0;
  }

  select {
    padding: 0 10px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    font-size: 12px;
    background: #fff;
    min-width: 140px;
    height: 28px;
    color: #344054;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: #556ee6;
    }
  }

  .orientation-btns {
    display: flex;
    align-items: center;
    gap: 2px;
    height: 28px;
  }

  .orientation-btn {
    width: 32px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e9ecef;
    background: #fff;
    border-radius: 4px;
    cursor: pointer;
    color: #6c757d;
    transition: all 0.15s;

    &:hover {
      border-color: #556ee6;
      color: #556ee6;
    }

    &.active {
      background: #556ee6;
      border-color: #556ee6;
      color: #fff;
    }

    svg {
      display: block;
    }
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    border-color: #556ee6;
    color: #556ee6;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  i {
    font-size: 16px;
  }
}

.header-divider {
  width: 1px;
  height: 24px;
  background: #e9ecef;
  margin: 0 6px;
}

.btn-secondary {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 12px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #556ee6;
    color: #556ee6;
  }
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 16px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: #485ec4;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

// Layout Principal
.builder-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

// Sidebar Esquerda
.sidebar-left {
  width: 260px;
  background: #fff;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  .sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid #e9ecef;

    h3 {
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      letter-spacing: 0.5px;
      margin: 0;
      text-transform: uppercase;
    }

    .btn-add {
      width: 28px;
      height: 28px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      border: none;
      background: #556ee6;
      color: #fff;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background: #485ec4;
      }
    }
  }

  .search-box {
    padding: 12px;
    position: relative;

    i {
      position: absolute;
      left: 22px;
      top: 50%;
      transform: translateY(-50%);
      color: #adb5bd;
      font-size: 16px;
    }

    input {
      width: 100%;
      padding: 8px 12px 8px 36px;
      border: 1px solid #e9ecef;
      border-radius: 6px;
      font-size: 13px;

      &:focus {
        outline: none;
        border-color: #556ee6;
      }
    }
  }

  .templates-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 12px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #d0d5dd;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: #b0b5bd;
    }
  }

  .template-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      border-color: #556ee6;
      box-shadow: 0 2px 8px rgba(85, 110, 230, 0.1);
    }

    &.active {
      border-color: #556ee6;
      background: #f8f9ff;
      box-shadow: 0 2px 8px rgba(85, 110, 230, 0.15);
    }

    &.preset-card {
      border-style: dashed;

      &:hover {
        border-style: solid;
        background: #f8f9ff;
      }

      .preset-thumbnail {
        width: 100%;
        height: 80px;
        object-fit: contain;
        background: #f8f9fa;
      }

      .preset-hint {
        font-size: 11px;
        color: #556ee6;

        i {
          margin-right: 4px;
        }
      }
    }

    .template-header {
      display: flex;
      justify-content: space-between;
      padding: 12px;

      .template-info {
        h4 {
          font-size: 13px;
          font-weight: 600;
          margin: 0 0 2px;
          color: #344054;
        }
        .template-size {
          font-size: 11px;
          color: #6c757d;
        }
      }

      .template-menu-wrapper {
        position: relative;
      }

      .template-menu {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #adb5bd;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.15s;
        font-size: 18px;

        &:hover {
          background: #f8f9fa;
          color: #556ee6;
        }
      }

      .template-dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        min-width: 180px;
        z-index: 100;
        padding: 6px 0;

        button {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 14px;
          border: none;
          background: none;
          font-size: 13px;
          color: #344054;
          cursor: pointer;
          text-align: left;
          transition: all 0.15s;

          i {
            font-size: 16px;
            color: #6c757d;
          }

          &:hover {
            background: #f8f9fa;
          }

          &.danger {
            color: #dc3545;

            i {
              color: #dc3545;
            }

            &:hover {
              background: #fff5f5;
            }
          }
        }
      }
    }

    .template-preview {
      padding: 4px 12px;

      .preview-badge {
        background: #f8f9fa;
        border: 1px dashed #dee2e6;
        border-radius: 6px;
        min-height: 50px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #adb5bd;

        i { font-size: 20px; margin-bottom: 2px; }
        span { font-size: 10px; }
      }
    }

    .template-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      border-top: 1px solid #f0f0f0;

      .modified {
        font-size: 10px;
        color: #adb5bd;
      }

      .badge-active {
        font-size: 10px;
        padding: 2px 8px;
        background: #d1fae5;
        color: #059669;
        border-radius: 10px;
        font-weight: 500;
      }

      .badge-default {
        font-size: 10px;
        padding: 2px 8px;
        background: #dbeafe;
        color: #2563eb;
        border-radius: 10px;
        font-weight: 500;
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #adb5bd;

    i { font-size: 40px; margin-bottom: 12px; display: block; }
    p { font-size: 13px; margin: 0; }
  }
}

// Area do Canvas
.canvas-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;

  .canvas-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    background: #fff;
    border-bottom: 1px solid #e9ecef;
    flex-shrink: 0;

    .toolbar-left,
    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .zoom-level {
      min-width: 50px;
      text-align: center;
      font-size: 13px;
      color: #6c757d;
      font-weight: 500;
    }

    .toolbar-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: 1px solid #e9ecef;
      background: #fff;
      border-radius: 6px;
      color: #6c757d;
      cursor: pointer;
      transition: all 0.15s;

      &:hover {
        border-color: #556ee6;
        color: #556ee6;
      }
    }

    .grid-toggle {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: #6c757d;
      cursor: pointer;
      padding: 6px 10px;
      border-radius: 6px;
      transition: background 0.15s;

      &:hover {
        background: #f8f9fa;
      }

      input {
        cursor: pointer;
        accent-color: #556ee6;
      }
    }
  }

  .canvas-container {
    flex: 1;
    overflow: auto;
    padding: 1.5rem;
    background: #e5e5e5;
    position: relative;

    .ruler {
      position: absolute;
      background: #f8f9fa;
      z-index: 10;

      .ruler-mark {
        position: absolute;
        font-size: 0.5rem;
        color: #adb5bd;
      }

      &.ruler-horizontal {
        top: 0;
        left: 25px;
        right: 0;
        height: 18px;
        border-bottom: 1px solid #dee2e6;

        .ruler-mark {
          border-left: 1px solid #dee2e6;
          height: 100%;
          span { padding-left: 2px; }
        }
      }

      &.ruler-vertical {
        top: 18px;
        left: 0;
        bottom: 0;
        width: 25px;
        border-right: 1px solid #dee2e6;

        .ruler-mark {
          border-top: 1px solid #dee2e6;
          width: 100%;
          text-align: right;
          span { padding-right: 3px; font-size: 0.5rem; }
        }
      }
    }

    .badge-canvas {
      background: #fff;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
      position: relative;
      margin-left: 25px;
      margin-top: 18px;

      .grid-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
      }

      .canvas-element {
        cursor: move;
        border: 1px solid transparent;
        box-sizing: border-box;
        background: rgba(255,255,255,0.5);

        &:hover { border-color: rgba(85, 110, 230, 0.5); }
        &.selected {
          border: 2px solid #556ee6;
          box-shadow: 0 0 0 2px rgba(85, 110, 230, 0.2);
        }
        &.locked { cursor: not-allowed; }
        &.hidden { opacity: 0.3; }

        .element-content {
          padding: 2px;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;

          .qrcode-placeholder,
          .photo-placeholder {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            background: #f8f9fa;
            border: 1px dashed #dee2e6;
            color: #6c757d;

            i { margin-bottom: 0.25rem; }
            small { font-size: 0.625rem; }
          }
        }

        .resize-handle {
          position: absolute;
          width: 8px;
          height: 8px;
          background: #556ee6;
          border: 1px solid #fff;
          border-radius: 2px;

          &.nw { top: -4px; left: -4px; cursor: nw-resize; }
          &.ne { top: -4px; right: -4px; cursor: ne-resize; }
          &.sw { bottom: -4px; left: -4px; cursor: sw-resize; }
          &.se { bottom: -4px; right: -4px; cursor: se-resize; }
        }
      }
    }

    .no-size-selected {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 300px;
      color: #6c757d;
      background: #fff;
      border-radius: 8px;
      margin: 2rem auto;
      max-width: 400px;

      i { font-size: 3rem; margin-bottom: 1rem; color: #adb5bd; }
      p { font-size: 0.9375rem; text-align: center; margin: 0; }
    }
  }

  .canvas-footer {
    display: flex;
    justify-content: space-between;
    padding: 8px 16px;
    background: #fff;
    border-top: 1px solid #e9ecef;
    font-size: 12px;
    color: #6c757d;
    flex-shrink: 0;

    .footer-left,
    .footer-right {
      display: flex;
      align-items: center;
    }

    .separator {
      margin: 0 10px;
      color: #dee2e6;
    }
  }
}

// Sidebar Direita
.sidebar-right {
  width: 280px;
  background: #fff;
  border-left: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  .sidebar-tabs {
    display: flex;
    border-bottom: 1px solid #e9ecef;
    flex-shrink: 0;

    .tab-btn {
      flex: 1;
      padding: 12px;
      border: none;
      background: transparent;
      font-size: 13px;
      color: #6c757d;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.15s;

      &:hover { background: #f8f9fa; }
      &.active {
        color: #556ee6;
        border-bottom: 2px solid #556ee6;
        margin-bottom: -1px;
        font-weight: 500;
      }
    }
  }

  .tab-content {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
  }

  .panel-section {
    margin-bottom: 20px;
    flex-shrink: 0;

    &.panel-section-grow {
      flex: 1;
      display: flex;
      flex-direction: column;
      margin-bottom: 0;
      min-height: 0;
    }

    h4 {
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      letter-spacing: 0.5px;
      margin-bottom: 10px;
      text-transform: uppercase;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;

      h4 { margin: 0; }
    }
  }

  .elements-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;

    .element-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 12px 8px;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      cursor: grab;
      transition: all 0.15s;

      &:hover {
        border-color: #556ee6;
        background: #f8f9ff;
      }

      i { font-size: 20px; color: #6c757d; margin-bottom: 4px; }
      span { font-size: 11px; color: #6c757d; }
    }
  }

  .loading-fields {
    text-align: center;
    padding: 20px;
    color: #6c757d;
    font-size: 13px;

    i { margin-right: 8px; }
  }

  .dynamic-fields-list {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .field-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.15s;

      &:hover {
        border-color: #556ee6;
        background: #f8f9ff;
        box-shadow: 0 2px 4px rgba(85, 110, 230, 0.1);
      }

      .field-name {
        flex: 1;
        font-size: 13px;
        font-weight: 500;
        color: #344054;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .btn-add-field {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: #556ee6;
        color: #fff;
        border-radius: 6px;
        cursor: pointer;
        opacity: 0;
        transition: all 0.15s;
        flex-shrink: 0;

        &:hover {
          background: #485ec4;
        }
      }

      &:hover .btn-add-field {
        opacity: 1;
      }
    }
  }

  .layers-list {
    max-height: 200px;
    overflow-y: auto;

    .layer-item {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s;
      border: 1px solid transparent;

      &:hover { background: #f8f9fa; }
      &.selected {
        background: #eef2ff;
        border-color: #556ee6;
      }

      .layer-drag-handle {
        cursor: grab;
        color: #adb5bd;
        font-size: 0.875rem;
      }

      .layer-name {
        flex: 1;
        font-size: 0.75rem;
        color: #2c3e50;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .layer-actions {
        display: flex;
        gap: 0.125rem;

        i {
          padding: 0.125rem;
          color: #adb5bd;
          cursor: pointer;
          font-size: 0.875rem;

          &:hover { color: #556ee6; }
        }
      }
    }
  }

  .empty-layers {
    text-align: center;
    padding: 1rem;
    color: #adb5bd;

    p { font-size: 0.75rem; margin: 0; }
  }

  .properties-panel {
    .form-group {
      margin-bottom: 16px;

      label {
        display: block;
        font-size: 11px;
        font-weight: 500;
        color: #6c757d;
        margin-bottom: 6px;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }

      input, select {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #e9ecef;
        border-radius: 6px;
        font-size: 13px;
        color: #344054;
        transition: border-color 0.15s;

        &:focus {
          outline: none;
          border-color: #556ee6;
        }
      }

      input[type="color"] {
        height: 36px;
        padding: 4px;
        cursor: pointer;
      }
    }

    .properties-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;

      .prop-item {
        label {
          display: block;
          font-size: 10px;
          font-weight: 500;
          color: #adb5bd;
          margin-bottom: 4px;
          text-transform: uppercase;
        }

        input {
          width: 100%;
          padding: 6px 10px;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          font-size: 13px;
          color: #344054;

          &:focus {
            outline: none;
            border-color: #556ee6;
          }
        }
      }
    }

    .btn-group-props {
      display: flex;
      gap: 4px;

      .btn-prop {
        width: 36px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #e9ecef;
        background: #fff;
        color: #6c757d;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s;

        &:hover {
          border-color: #556ee6;
          color: #556ee6;
        }

        &.active {
          background: #556ee6;
          border-color: #556ee6;
          color: #fff;
        }
      }
    }
  }

  .prop-hint {
    display: block;
    margin-top: 6px;
    font-size: 11px;
    line-height: 1.35;
    color: #74788d;
  }

  .no-selection {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #adb5bd;

    i { font-size: 40px; margin-bottom: 12px; }
    p { font-size: 13px; text-align: center; margin: 0; }
  }
}

// Botao de deletar elemento
.btn-danger {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: #dc3545;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #c82333;
  }
}
</style>

<style lang="scss">
/* Global styles for badge builder page - not scoped */
body.badge-builder-page {
  overflow: hidden !important;
  height: 100vh !important;
}

body.badge-builder-page footer.footer {
  display: none !important;
}

body.badge-builder-page .main-content {
  padding: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  margin-right: 0 !important;
  overflow: hidden !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
}

body.badge-builder-page .page-content {
  padding: 0 !important;
  margin: 0 !important;
  margin-top: 70px !important;
  overflow: hidden !important;
  min-height: 0 !important;
  height: calc(100vh - 70px) !important;
  max-height: calc(100vh - 70px) !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

body.badge-builder-page .page-content > .container-fluid {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  min-height: 0 !important;
  max-width: 100% !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}
</style>
